import { ColDef } from 'ag-grid-community';
import { Dictionary } from 'lodash';
import moment from 'moment';
import { useCallback, useContext, useMemo } from 'react';
import { DateType } from 'api/models';
import {
  ProjectFinancialsGroupBy,
  ProjectFinancialsDateRange,
  ProjectFinancialsZoomLevel,
  ProjectFinancialsContractType,
} from 'api/project';
import { StatusFieldOption } from 'api/tasks';
import { ProjectFinancialsContext } from 'contexts/project-financials';
import { useTranslate } from 'hooks/use-translation';
import {
  budgetFormatter as budgetFormatterOg,
  costGetter,
  hoursFormatter,
  marginGetter,
  percentageFormatter,
  profitGetter,
  revenueGetter,
  timeAllocatedGetter,
  timeTrackedGetter,
} from '../components/table-renderers';
import { getDateCategoriesBasedOnDuration } from '../utils/common';
import { useProjectFinancialsLabels } from './labels';

interface Props {
  budgetTableRange: ProjectFinancialsDateRange;
  hoursTableRange: ProjectFinancialsDateRange;
  budgetTableZoomLevel: ProjectFinancialsZoomLevel;
  hoursTableZoomLevel: ProjectFinancialsZoomLevel;
  budgetTableGroupBy: ProjectFinancialsGroupBy;
  hoursTableGroupBy: ProjectFinancialsGroupBy;
  taskStatusOptionsLookup: Dictionary<StatusFieldOption>;
}

interface ReturnProps {
  budgetColumnDefs: ColDef[];
  hoursColumnDefs: ColDef[];
  budgetAutoGroupColumnDef: ColDef;
  hoursAutoGroupColumnDef: ColDef;
}

export const useProjectFinancialsTableColumnDefinitions = ({
  budgetTableRange,
  hoursTableRange,
  budgetTableZoomLevel,
  hoursTableZoomLevel,
  budgetTableGroupBy,
  hoursTableGroupBy,
  taskStatusOptionsLookup,
}: Props): ReturnProps => {
  const translate = useTranslate();

  const context = useContext(ProjectFinancialsContext);
  const {
    currencyCode,
    project: { autoAllocation },
    projectFinancials: {
      projectFinancialDetails: { financialContractType },
    },
    projectFinancialsVisibilityConfig: { profitabilityData, allocationData },
    permissionsMap: {
      projectProfitability: { canRead: canViewProfitability },
    },
  } = context;

  const {
    revenueLabel,
    estimatedRevenueLabel,
    actualRevenueLabel,
    actualLabel,
    estimatedLabel,
    costLabel,
    profitLabel,
    marginLabel,
    timeTrackedLabel,
    timeAllocatedLabel,
  } = useProjectFinancialsLabels({ financialContractType });

  const budgetFormatter = useCallback(
    (val) => budgetFormatterOg(val, currencyCode),
    [currencyCode],
  );

  const budgetAutoGroupColumnDef: ColDef = useMemo(
    () => ({
      minWidth: 360,
      pinned: true,
      headerComponent: 'groupHeaderRenderer',
      headerComponentParams: {
        groupBy: budgetTableGroupBy,
      },
      cellClass: 'last-cell-column-in-group',
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: 'groupCellRenderer',
        innerRendererParams: {
          groupBy: budgetTableGroupBy,
          taskStatusOptionsLookup,
        },
      },
    }),
    [budgetTableGroupBy, taskStatusOptionsLookup],
  );

  const hoursAutoGroupColumnDef: ColDef = useMemo(
    () => ({
      minWidth: 360,
      pinned: true,
      headerComponent: 'groupHeaderRenderer',
      headerComponentParams: {
        groupBy: hoursTableGroupBy,
      },
      cellClass: 'last-cell-column-in-group',
      cellRendererParams: {
        suppressCount: true,
        cellClass: 'last-cell-column-in-group',
        innerRenderer: 'groupCellRenderer',
        innerRendererParams: {
          groupBy: hoursTableGroupBy,
          taskStatusOptionsLookup,
        },
      },
    }),
    [hoursTableGroupBy, taskStatusOptionsLookup],
  );

  const topLevelHeaderLabel = useCallback(
    (zoomLevel: ProjectFinancialsZoomLevel, date: DateType, index: number) => {
      let headerName = '';

      if (
        zoomLevel === ProjectFinancialsZoomLevel.Revenue ||
        zoomLevel === ProjectFinancialsZoomLevel.Profitability
      ) {
        // This is to fix translation key
        return headerName;
      }

      if (zoomLevel === ProjectFinancialsZoomLevel.Overall) {
        headerName = translate(`project.financials.table.header.${zoomLevel}`);
      }
      const dateMoment = moment(date);
      const nextDateMoment =
        zoomLevel === ProjectFinancialsZoomLevel.Monthly
          ? dateMoment.add(1, 'month')
          : dateMoment.endOf('quarter'); // TODO: handle period

      headerName = translate(`project.financials.table.header.${zoomLevel}`, {
        index:
          zoomLevel === ProjectFinancialsZoomLevel.Monthly
            ? index
            : dateMoment.get('quarter'),
        // TODO: needs to be fine-tuned for quarter and period
        date:
          zoomLevel === ProjectFinancialsZoomLevel.Monthly
            ? dateMoment.format("MMM 'YY")
            : `${dateMoment.format("MMM 'YY")} - ${nextDateMoment.format(
                "MMM 'YY",
              )}`,
      });

      return headerName;
    },

    [translate],
  );

  const getRevenueColumn = useCallback(
    (
      headerName: string,
      date: DateType,
      isAutoAllocationEnabled = false,
    ): ColDef => ({
      headerName,
      headerClass: 'field-header',
      ...(profitabilityData && allocationData
        ? {}
        : (!profitabilityData && !allocationData) || isAutoAllocationEnabled
        ? {
            cellClass: 'last-cell-column-in-group',
          }
        : {}),
      valueGetter: (params) =>
        revenueGetter(
          params,
          date,
          budgetTableGroupBy,
          isAutoAllocationEnabled,
        ),
      valueFormatter: budgetFormatter,
    }),
    [allocationData, budgetFormatter, budgetTableGroupBy, profitabilityData],
  );

  const getProfitabilityColumns = useCallback(
    (date: DateType, isAutoAllocationEnabled = false): ColDef[] => [
      {
        headerName: costLabel,
        headerClass: 'field-header',
        ...(financialContractType === ProjectFinancialsContractType.TM
          ? {}
          : {
              cellClass: 'last-cell-column-in-group',
            }),
        valueGetter: (params) =>
          costGetter(params, date, budgetTableGroupBy, isAutoAllocationEnabled),
        valueFormatter: budgetFormatter,
      },
      ...(financialContractType === ProjectFinancialsContractType.TM
        ? [
            {
              headerName: profitLabel,
              headerClass: 'field-header',
              valueGetter: (params) =>
                profitGetter(
                  params,
                  date,
                  budgetTableGroupBy,
                  isAutoAllocationEnabled,
                ),
              valueFormatter: budgetFormatter,
            },
            {
              headerName: marginLabel,
              headerClass: 'field-header',
              cellClass: 'last-cell-column-in-group',
              valueGetter: (params) =>
                marginGetter(
                  params,
                  date,
                  budgetTableGroupBy,
                  isAutoAllocationEnabled,
                ),
              valueFormatter: percentageFormatter,
            },
          ]
        : []),
    ],
    [
      budgetFormatter,
      budgetTableGroupBy,
      costLabel,
      financialContractType,
      marginLabel,
      profitLabel,
    ],
  );

  const budgetChildColumns = useCallback(
    (date: DateType, isAutoAllocationEnabled = false): ColDef[] => [
      ...(financialContractType !== ProjectFinancialsContractType.NONBILLABLE
        ? [getRevenueColumn(revenueLabel, date, isAutoAllocationEnabled)]
        : []),
      ...(canViewProfitability && profitabilityData
        ? getProfitabilityColumns(date, isAutoAllocationEnabled)
        : []),
    ],

    [
      canViewProfitability,
      financialContractType,
      getProfitabilityColumns,
      getRevenueColumn,
      profitabilityData,
      revenueLabel,
    ],
  );

  const budgetColumnDefs: ColDef[] = useMemo(
    () =>
      getDateCategoriesBasedOnDuration(
        budgetTableRange,
        budgetTableZoomLevel,
      ).map((date, index) => ({
        headerName: topLevelHeaderLabel(budgetTableZoomLevel, date, index + 1),
        headerGroupComponent: 'columnHeaderRenderer',
        headerClass: 'periodicity-header',
        children:
          !!autoAllocation && allocationData
            ? canViewProfitability && profitabilityData
              ? [
                  {
                    headerName: actualLabel,
                    headerGroupComponent: 'columnHeaderRenderer',
                    headerClass: 'actuality-header',
                    children: budgetChildColumns(date),
                  },

                  {
                    headerName: estimatedLabel,
                    headerGroupComponent: 'columnHeaderRenderer',
                    headerClass: 'actuality-header',
                    children: budgetChildColumns(date, autoAllocation),
                  },
                ]
              : [
                  getRevenueColumn(actualRevenueLabel, date),
                  getRevenueColumn(estimatedRevenueLabel, date, autoAllocation),
                ]
            : budgetChildColumns(date),
      })),
    [
      actualLabel,
      actualRevenueLabel,
      allocationData,
      autoAllocation,
      budgetChildColumns,
      budgetTableRange,
      budgetTableZoomLevel,
      canViewProfitability,
      estimatedLabel,
      estimatedRevenueLabel,
      getRevenueColumn,
      profitabilityData,
      topLevelHeaderLabel,
    ],
  );

  const hoursColumnDefs: ColDef[] = useMemo(
    () =>
      getDateCategoriesBasedOnDuration(
        hoursTableRange,
        hoursTableZoomLevel,
      ).map((date, index) => ({
        headerName: topLevelHeaderLabel(hoursTableZoomLevel, date, index + 1),
        headerGroupComponent: 'columnHeaderRenderer',
        headerClass: 'periodicity-header',
        children: [
          {
            headerName: timeTrackedLabel,
            headerGroupComponent: 'columnHeaderRenderer',
            headerClass: 'field-header',
            ...(autoAllocation && allocationData
              ? {}
              : {
                  cellClass: 'last-cell-column-in-group',
                }),
            valueGetter: (params) => timeTrackedGetter(params, date),
            valueFormatter: hoursFormatter,
          },
          ...(autoAllocation && allocationData
            ? [
                {
                  headerName: timeAllocatedLabel,
                  headerGroupComponent: 'columnHeaderRenderer',
                  headerClass: 'field-header',
                  cellClass: 'last-cell-column-in-group',
                  valueGetter: (params) =>
                    timeAllocatedGetter(params, date, hoursTableGroupBy),
                  valueFormatter: hoursFormatter,
                },
              ]
            : []),
        ],
      })),
    [
      allocationData,
      autoAllocation,
      hoursTableGroupBy,
      hoursTableRange,
      hoursTableZoomLevel,
      timeAllocatedLabel,
      timeTrackedLabel,
      topLevelHeaderLabel,
    ],
  );

  return {
    budgetColumnDefs,
    hoursColumnDefs,
    budgetAutoGroupColumnDef,
    hoursAutoGroupColumnDef,
  };
};
